import {
  Button,
  Container,
  Divider,
  Grid,
  Group,
  PasswordInput,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { Check, CircleCheck, Copy, X } from 'tabler-icons-react';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import { formatDate3LetterMonth } from '../../helpers/date-convert';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import {
  BasicMIDs,
  RemoveMIDsRequest,
  RetrieveAssignedMIDsToPOSResponse,
} from '../../type/mids.types';
import { RetrievePOSResponse } from '../../type/pos.types';
import StatusText from '../admin-table/components/status-text.component';
import BackButton from '../buttons/back-button.component';
import MIDsTable from '../mid/components/mids-table';
import XCol3 from '../x-components/grid-col/x-col-3.component';
import XCol6 from '../x-components/grid-col/x-col-6.component';
import XTextStackInfo from '../x-components/x-text-stack-info.component';
import SkeletonViewAllPOSInfo from './skeleton-loading/skeleton-view-all-pos.component';

function ViewPosInfo(props: Props) {
  const { setComponentManager, selectedPOSId, setSelectedPOS } = props;
  const [posData, setPosData] = useState<RetrievePOSResponse>();
  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);
  const { auth } = useAuth();
  const activationCodeClipboard = useClipboard();
  const authPassClipboard = useClipboard();
  const secretKeyClipboard = useClipboard();
  const [assignedMIDsOpenComponent, setAssignedMIDsOpenComponent] = useState(
    OpenComponent.HOME
  );
  const [selectedMIDId, setselectedMIDId] = useState<number>();
  const [isLoadingAssignedMIDs, setIsLoadingAssignedMIDs] = useState(false);
  const [isLoadingRemoveMIDs, setIsLoadingRemoveMIDs] = useState(false);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [midsData, setMidsData] = useState<BasicMIDs[]>();
  const isViewOnly =
    auth?.role === Roles.SETTLEMENT || auth?.role === Roles.CHECKER;

  const loadAssignedMIDs = async () => {
    try {
      setIsLoadingAssignedMIDs(true);

      const response = await axiosPrivate.get(
        `/web/mids/${selectedPOSId}/mids`
      );
      const responseData: RetrieveAssignedMIDsToPOSResponse = response.data;
      const assignedMIDsData: BasicMIDs[] = responseData.items;

      setMidsData(assignedMIDsData);
      setIsChecked([]);
      setCheckedAll(false);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;
      showNotification({
        id: 'failed-to-retrieve-assigned-mids',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoadingAssignedMIDs(false);
    }
  };

  useEffect(() => {
    const loadSelectedPOS = async () => {
      try {
        setIsLoading(true);

        const response = await axiosPrivate.get(`/web/pos/${selectedPOSId}`);
        const posData: RetrievePOSResponse = response.data;

        setPosData(posData);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;
        showNotification({
          id: 'failed-to-retrieve-pos',
          message:
            error?.error ||
            err.response?.data?.error ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSelectedPOS();
    loadAssignedMIDs();
  }, []);

  const handleRemoveMIDs = async () => {
    try {
      setIsLoadingAssignedMIDs(true);

      const req: RemoveMIDsRequest = {
        posId: selectedPOSId,
        midIds: isChecked,
      };

      await axiosPrivate.post(`/web/mids/remove`, req);
      loadAssignedMIDs();

      showNotification({
        id: 'successfully-removed-mids',
        message: 'Successfully removed MID',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;
      showNotification({
        id: 'failed-to-retrieve-assigned-mids',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoadingAssignedMIDs(false);
    }
  };

  const labels = {
    partner: 'Partner',
    reseller: 'Reseller',
    merchant: 'Merchant',
    branch: 'Branch',
    activationCode: 'Activation code',
    activationStatus: 'Activation status',
    activationDate: 'Activation date',
    authPass: 'Auth pass',
    secretKey: 'Secret key',
    posName: 'POS name',
    isActive: 'Active status',
  };

  return (
    <>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
          setSelectedPOS(undefined);
        }}
      />
      <Title order={2} my='lg'>
        POS Information
      </Title>

      <Divider my='xl' />
      <Container px={0} fluid>
        {isLoading ? (
          <SkeletonViewAllPOSInfo />
        ) : (
          <Grid>
            <XCol3>
              <XTextStackInfo
                label={labels.partner}
                value={posData?.association.partnerName}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.reseller}
                value={posData?.association.resellerName}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.merchant}
                value={posData?.association.merchantName}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.branch}
                value={posData?.association.branchName}
              />
            </XCol3>

            <XCol3>
              <XTextStackInfo label={labels.posName} value={posData?.posName} />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.isActive}
                value={
                  <StatusText
                    status={posData?.isActive ? 'ENABLED' : 'DISABLED'}
                  />
                }
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.activationStatus}
                value={
                  <StatusText
                    status={
                      posData?.activationCode.activationStatus
                        ? 'ACTIVATED'
                        : 'NOT_ACTIVATED'
                    }
                  />
                }
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.activationDate}
                value={formatDate3LetterMonth(
                  posData?.activationCode.dateActivated?.toString()
                )}
              />
            </XCol3>

            <XCol6>
              <Tooltip
                position='right'
                placement='center'
                gutter={13}
                label={
                  activationCodeClipboard.copied ? (
                    <Group spacing={2}>
                      <CircleCheck size={18} strokeWidth={1.5} />
                      <Text size='sm'>Copied</Text>
                    </Group>
                  ) : (
                    <Group spacing={2}>
                      <Copy size={18} strokeWidth={1.5} />
                      <Text size='sm'>Click to copy</Text>
                    </Group>
                  )
                }
                style={{ width: '100%' }}
                withArrow
                color={activationCodeClipboard.copied ? 'green' : 'gray'}
              >
                <TextInput
                  label={labels.activationCode}
                  variant='filled'
                  value={posData?.activationCode.code}
                  readOnly
                  rightSectionWidth={116}
                  mt='md'
                  styles={{ input: { cursor: 'pointer' } }}
                  onClick={() =>
                    activationCodeClipboard.copy(posData?.activationCode.code)
                  }
                />
              </Tooltip>
            </XCol6>
            <XCol6></XCol6>
            {!isViewOnly && (
              <>
                <XCol6>
                  <Tooltip
                    position='right'
                    placement='center'
                    gutter={13}
                    label={
                      authPassClipboard.copied ? (
                        <Group spacing={2}>
                          <CircleCheck size={18} strokeWidth={1.5} />
                          <Text size='sm'>Copied</Text>
                        </Group>
                      ) : (
                        <Group spacing={2}>
                          <Copy size={18} strokeWidth={1.5} />
                          <Text size='sm'>Click to copy</Text>
                        </Group>
                      )
                    }
                    style={{ width: '100%' }}
                    withArrow
                    color={authPassClipboard.copied ? 'green' : 'gray'}
                  >
                    <PasswordInput
                      label={labels.authPass}
                      variant='filled'
                      value={posData?.authPass}
                      readOnly
                      rightSectionWidth={116}
                      mt='md'
                      styles={{ innerInput: { cursor: 'pointer' } }}
                      onClick={() => authPassClipboard.copy(posData?.authPass)}
                    />
                  </Tooltip>
                </XCol6>
                <XCol6>
                  <Tooltip
                    position='right'
                    placement='center'
                    gutter={13}
                    label={
                      secretKeyClipboard.copied ? (
                        <Group spacing={2}>
                          <CircleCheck size={18} strokeWidth={1.5} />
                          <Text size='sm'>Copied</Text>
                        </Group>
                      ) : (
                        <Group spacing={2}>
                          <Copy size={18} strokeWidth={1.5} />
                          <Text size='sm'>Click to copy</Text>
                        </Group>
                      )
                    }
                    style={{ width: '100%' }}
                    withArrow
                    color={secretKeyClipboard.copied ? 'green' : 'gray'}
                  >
                    <PasswordInput
                      label={labels.secretKey}
                      variant='filled'
                      value={posData?.secretKey}
                      readOnly
                      rightSectionWidth={116}
                      mt='md'
                      styles={{ innerInput: { cursor: 'pointer' } }}
                      onClick={() =>
                        secretKeyClipboard.copy(posData?.secretKey)
                      }
                    />
                  </Tooltip>
                </XCol6>
              </>
            )}
          </Grid>
        )}

        <Group position='apart' mt='lg'>
          <Title order={3}>Assigned MIDs</Title>

          <Button
            onClick={handleRemoveMIDs}
            loading={isLoadingRemoveMIDs}
            disabled={isChecked.length === 0}
          >
            REMOVE MID(s)
          </Button>
        </Group>

        <Divider my='xl' />
        <MIDsTable
          mids={midsData}
          setComponentManager={setAssignedMIDsOpenComponent}
          setSelectedMIDId={setselectedMIDId}
          isLoading={isLoadingAssignedMIDs}
          checkedAll={checkedAll}
          setCheckedAll={setCheckedAll}
          isChecked={isChecked}
          setIsChecked={setIsChecked}
          isViewOnly={isViewOnly}
        />
      </Container>
    </>
  );
}
export default ViewPosInfo;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  selectedPOSId: number;
  setSelectedPOS: React.Dispatch<React.SetStateAction<number | undefined>>;
}
